import { NEW_PLAYER_MASCOT_ORDER, MascotId, PlayerAppearance } from './types';
import { isMascotId } from './catalog';

export function stablePlayerHash(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function mascotForSlot(index: number): MascotId {
  return NEW_PLAYER_MASCOT_ORDER[
    Math.abs(Math.trunc(index)) % NEW_PLAYER_MASCOT_ORDER.length
  ];
}

export function deterministicAppearance(
  playerId: string,
  slot = 0,
  colorCount = 8,
): PlayerAppearance {
  const safeColorCount = Math.max(1, Math.trunc(colorCount));
  return {
    mascotId: mascotForSlot(slot),
    colorIndex: Math.abs(Math.trunc(slot)) % safeColorCount,
  };
}

export function normalizePlayerAppearance(
  value: unknown,
  playerId: string,
  slot = 0,
  colorCount = 8,
): PlayerAppearance {
  const fallback = deterministicAppearance(playerId, slot, colorCount);
  if (!value || typeof value !== 'object') return fallback;
  const candidate = value as Partial<PlayerAppearance>;
  return {
    mascotId: isMascotId(candidate.mascotId) ? candidate.mascotId : fallback.mascotId,
    colorIndex:
      typeof candidate.colorIndex === 'number' && Number.isFinite(candidate.colorIndex)
        ? Math.abs(Math.trunc(candidate.colorIndex)) % Math.max(1, Math.trunc(colorCount))
        : fallback.colorIndex,
  };
}

export function nextAvailableAppearance(
  playerId: string,
  existing: readonly PlayerAppearance[],
  colorCount: number,
): PlayerAppearance {
  const usedMascots = new Set(existing.map((appearance) => appearance.mascotId));
  const usedColors = new Set(existing.map((appearance) => appearance.colorIndex));
  const mascotId =
    NEW_PLAYER_MASCOT_ORDER.find((candidate) => !usedMascots.has(candidate)) ??
    mascotForSlot(existing.length || stablePlayerHash(playerId));
  let colorIndex = 0;
  while (usedColors.has(colorIndex) && colorIndex < colorCount) colorIndex += 1;
  return { mascotId, colorIndex: colorIndex % Math.max(1, colorCount) };
}

export function bestEyeColor(bodyColor: string): '#111316' | '#ffffff' {
  const normalized = /^#[0-9a-f]{6}$/i.test(bodyColor) ? bodyColor.slice(1) : 'ffffff';
  const channels = [0, 2, 4].map((offset) => Number.parseInt(normalized.slice(offset, offset + 2), 16) / 255);
  const linear = channels.map((channel) =>
    channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4,
  );
  const luminance = 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
  return luminance > 0.42 ? '#111316' : '#ffffff';
}
